// Keyboard focus trap for modal dialogs. Keeps Tab / Shift+Tab cycling
// inside the ref'd container while active, auto-focuses the first
// interactive element (or the dialog itself) on open, and restores focus to whatever had it
// before activation when the dialog closes.
//
// Not suitable for persistent drawers — those need to release focus back
// to the surrounding list. Use on components with role="dialog".
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  active: boolean,
  // Capture return focus before moving to a caller's preferred initial target.
  initialFocus: 'first' | 'container' = 'first',
): void {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const container = containerRef.current;
    if (!container) return;
    const addedTabIndex = !container.hasAttribute('tabindex');
    if (addedTabIndex) container.tabIndex = -1;

    const focusables = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(el => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);

    // Move focus inside unless it's already there (e.g. autoFocus handled it).
    if (!container.contains(document.activeElement)) {
      const target = initialFocus === 'container' ? container : focusables()[0];
      (target ?? container).focus();
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (document.activeElement === container) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    return () => {
      container.removeEventListener('keydown', handler);
      // A caller can supply its own tabindex while the trap is active.
      // Remove only the temporary value this hook still owns.
      if (addedTabIndex && container.getAttribute('tabindex') === '-1') {
        container.removeAttribute('tabindex');
      }
      const ret = returnFocusRef.current;
      if (ret && typeof ret.focus === 'function') {
        try {
          ret.focus();
        } catch {
          // Ref target may have been unmounted; silent fallback — focus
          // stays on <body> which is the browser default behavior anyway.
        }
      }
    };
  }, [active, containerRef, initialFocus]);
}
